import React from "react";
import Badge from "../Components/Badge";
import { ticketStatusBadgeVariant } from "../constants/ticketStatusBadge";
import ExpandableTicketMessageBody from "./ExpandableTicketMessageBody";
import TicketReplyForm from "./TicketReplyForm";

function MetadataRow({ label, children }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-1 border-t border-[var(--color-border)] py-3 first:border-t-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function MessageAttachments({ attachments = [] }) {
  if (attachments.length === 0) return null;

  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {attachments.map((attachment) => (
        <li key={attachment.id ?? attachment.url}>
          <a
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text)] hover:bg-[var(--color-surface-hover,rgba(0,0,0,0.04))]"
          >
            <span aria-hidden="true">📎</span>
            <span className="max-w-[16rem] truncate">
              {attachment.filename}
            </span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function ChatMessage({ message }) {
  return (
    <article className="mr-8 border-t border-[var(--color-border)] py-2">
      <header className="mb-1 flex items-baseline justify-between gap-2 text-xs text-muted">
        <span className="font-semibold text-[var(--color-text)]">
          {message.author_name}
        </span>
        <time dateTime={message.posted_at_label}>
          {message.posted_at_label}
        </time>
      </header>
      <ExpandableTicketMessageBody body={message.body} collapsedLines={1} />
      <MessageAttachments attachments={message.attachments} />
    </article>
  );
}

export default function TicketDetailContent({
  id: ticketId,
  ticket_number: ticketNumber,
  summary,
  status,
  status_name: statusName,
  description,
  owner_line: ownerLine,
  contact_line: contactLine,
  opened_at_label: openedAtLabel,
  last_updated_at_label: lastUpdatedAtLabel,
  attachments = [],
  messages = [],
  onMessageCreated,
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-b border-[var(--color-border)] px-6 py-5">
        <p className="text-lg font-semibold">{ticketNumber}</p>
        <p className="mt-1 text-sm text-muted">{summary}</p>
        <div className="mt-3">
          <Badge variant={ticketStatusBadgeVariant(status, statusName)}>
            {statusName}
          </Badge>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <section className="mb-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
            Description
          </h3>
          {description && description !== "—" ? (
            <ExpandableTicketMessageBody
              body={description}
              collapsedLines={4}
            />
          ) : (
            <p className="text-sm text-muted">—</p>
          )}
          <MessageAttachments attachments={attachments} />
        </section>

        <dl>
          <MetadataRow label="Technician">{ownerLine}</MetadataRow>
          <MetadataRow label="Contact">{contactLine}</MetadataRow>
          <MetadataRow label="Opened">{openedAtLabel}</MetadataRow>
          <MetadataRow label="Last update">{lastUpdatedAtLabel}</MetadataRow>
        </dl>

        <section className="mt-6">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
            Chat
          </h3>
          {messages.length === 0 ? (
            <p className="text-sm text-muted">No messages yet.</p>
          ) : (
            <div>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
            </div>
          )}
        </section>
      </div>

      {ticketId && onMessageCreated ? (
        <TicketReplyForm
          ticketId={ticketId}
          onMessageCreated={onMessageCreated}
        />
      ) : null}
    </div>
  );
}
